import { memo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, Lock, BarChart3, Target, ExternalLink } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { useCookieConsent } from '@/hooks/useCookieConsent';

interface CookieCategory {
  id: 'necessary' | 'analytics' | 'marketing';
  title: string;
  description: string;
  icon: typeof Cookie;
  locked?: boolean;
  services: string[];
}

const COOKIE_CATEGORIES: CookieCategory[] = [
  {
    id: 'necessary',
    title: 'Nödvändiga',
    description: 'Dessa cookies är nödvändiga för att webbplatsen ska fungera korrekt. De möjliggör grundläggande funktioner som sidnavigering och åtkomst till säkra områden.',
    icon: Lock,
    locked: true,
    services: ['Autentisering (Supabase)', 'Transaktionsmail (Resend)'],
  },
  {
    id: 'analytics',
    title: 'Analys',
    description: 'Dessa cookies hjälper oss förstå hur besökare interagerar med webbplatsen genom att samla in och rapportera information anonymt.',
    icon: BarChart3,
    services: ['Google Analytics 4 (GA4)', 'Microsoft Clarity'],
  },
  {
    id: 'marketing',
    title: 'Marknadsföring',
    description: 'Dessa cookies används för att visa relevanta annonser och mäta effektiviteten av våra marknadsföringskampanjer.',
    icon: Target,
    services: ['Google Ads', 'Meta Pixel (Facebook/Instagram)', 'TikTok Pixel'],
  },
];

const CookieSettingsModal = memo(function CookieSettingsModal() {
  const { consent, showModal, setShowModal, savePreferences, acceptAll, rejectAll } = useCookieConsent();
  
  const [analytics, setAnalytics] = useState(consent?.analytics ?? false);
  const [marketing, setMarketing] = useState(consent?.marketing ?? false);
  
  // Sync state when consent changes
  useEffect(() => {
    if (consent) {
      setAnalytics(consent.analytics);
      setMarketing(consent.marketing);
    }
  }, [consent]);
  
  const handleSave = () => {
    savePreferences(analytics, marketing);
  };
  
  const handleAcceptAll = () => {
    setAnalytics(true);
    setMarketing(true);
    acceptAll();
  };
  
  const handleRejectAll = () => {
    setAnalytics(false);
    setMarketing(false);
    rejectAll();
  };
  
  return (
    <Dialog open={showModal} onOpenChange={setShowModal}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-gold/10 rounded-xl">
              <Cookie className="w-5 h-5 text-gold" />
            </div>
            <DialogTitle className="font-display">Cookieinställningar</DialogTitle>
          </div>
          <DialogDescription>
            Hantera dina cookie-preferenser. Du kan ändra dessa inställningar när som helst.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {COOKIE_CATEGORIES.map((category, index) => {
            const Icon = category.icon;
            const isChecked = category.id === 'necessary' 
              ? true 
              : category.id === 'analytics' 
                ? analytics 
                : marketing;
            const setChecked = category.id === 'analytics' 
              ? setAnalytics 
              : category.id === 'marketing' 
                ? setMarketing 
                : undefined;
            
            return (
              <div key={category.id}>
                {index > 0 && <Separator className="mb-4" />}
                <div className="flex items-start gap-3">
                  <div className="p-1.5 bg-muted rounded-lg shrink-0 mt-0.5">
                    <Icon className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-4 mb-1">
                      <Label 
                        htmlFor={`cookie-${category.id}`}
                        className="font-semibold text-foreground cursor-pointer"
                      >
                        {category.title}
                        {category.locked && (
                          <span className="ml-2 text-xs text-muted-foreground font-normal">
                            (alltid aktiv)
                          </span>
                        )}
                      </Label>
                      <Switch
                        id={`cookie-${category.id}`}
                        checked={isChecked}
                        onCheckedChange={setChecked}
                        disabled={category.locked}
                        aria-describedby={`cookie-${category.id}-desc`}
                      />
                    </div>
                    <p 
                      id={`cookie-${category.id}-desc`}
                      className="text-sm text-muted-foreground leading-relaxed mb-2"
                    >
                      {category.description}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {category.services.map(service => (
                        <span 
                          key={service}
                          className="text-xs bg-muted px-2 py-0.5 rounded-full text-muted-foreground"
                        >
                          {service}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        <div className="text-sm text-muted-foreground">
          <Link 
            to="/cookies" 
            className="inline-flex items-center gap-1 text-primary hover:underline"
            onClick={() => setShowModal(false)}
          >
            Läs vår fullständiga cookiepolicy
            <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
        
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={handleRejectAll} className="sm:mr-auto">
            Avvisa alla
          </Button>
          <Button variant="secondary" onClick={handleSave}>
            Spara val
          </Button>
          <Button variant="cta" onClick={handleAcceptAll}>
            Acceptera alla
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

export default CookieSettingsModal;
